import React, { useEffect, useRef, useState, useCallback } from 'react';
import { FiCopy, FiCheck } from 'react-icons/fi';
import './RealtimeCaption.css';

export interface TranscriptSegment {
  text: string;
  isPartial: boolean;
  words?: Array<{ text: string; start: number; end: number }>;
}

interface RealtimeCaptionProps {
  segments: TranscriptSegment[];
  partialText: string;
  isActive?: boolean;
  error?: string | null;
  onRetry?: () => void;
  className?: string;
}

const RealtimeCaption: React.FC<RealtimeCaptionProps> = ({
  segments,
  partialText,
  isActive = false,
  error = null,
  onRetry,
  className = ''
}) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const endRef = useRef<HTMLDivElement>(null);
  const [copied, setCopied] = useState(false);

  const fullText = [...segments.map((s) => s.text), partialText].filter(Boolean).join(' ');

  const handleCopy = useCallback(async () => {
    if (!fullText.trim()) return;
    try {
      await navigator.clipboard.writeText(fullText.trim());
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      setCopied(false);
    }
  }, [fullText]);

  useEffect(() => {
    endRef.current?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
  }, [segments, partialText]);

  return (
    <div className={`realtime-caption ${isActive ? 'active' : ''} ${className}`.trim()} ref={containerRef}>
      <div className="realtime-caption-header">
        <span className="caption-label">
          {isActive ? (
            <>
              <span className="live-dot" />
              Live Caption
            </>
          ) : (
            'Transcript'
          )}
        </span>
        <div className="caption-actions">
          {fullText.trim() && (
            <button
              type="button"
              className="caption-action-btn copy-btn"
              onClick={handleCopy}
              title={copied ? 'Copied!' : 'Copy transcript'}
              aria-label={copied ? 'Copied!' : 'Copy transcript'}
            >
              {copied ? (
                <>{React.createElement(FiCheck as React.ComponentType<{ size?: number }>, { size: 14 })} Copied</>
              ) : (
                <>{React.createElement(FiCopy as React.ComponentType<{ size?: number }>, { size: 14 })} Copy</>
              )}
            </button>
          )}
          {error && onRetry && (
            <button
              type="button"
              className="caption-action-btn retry-btn"
              onClick={onRetry}
              title="Retry caption connection"
            >
              Retry
            </button>
          )}
        </div>
      </div>
      <div className="realtime-caption-content" dir="auto">
        {error && (
          <div className="caption-error">
            {error}
          </div>
        )}
        {segments.length === 0 && !partialText && !error && (
          <div className="caption-placeholder">
            {isActive ? 'Start speaking...' : 'Transcript will appear here when recording'}
          </div>
        )}
        {segments.map((seg, i) => (
          <span key={i} className="caption-segment">
            {seg.text}
            {i < segments.length - 1 || partialText ? ' ' : ''}
          </span>
        ))}
        {partialText && (
          <span className="caption-partial">
            {partialText}
            <span className="cursor-blink">|</span>
          </span>
        )}
      </div>
      <div ref={endRef} />
    </div>
  );
};

export default RealtimeCaption;
